import {
  Box,
  Button,
  Chip,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { FC, useState } from 'react';
import { Employer } from '../types';
import {
  darkBlue,
  primaryGreen,
  primaryGrey,
  primaryRed,
  white,
  yellow,
} from '@styles/colors';
import EmployerMetricsModal from './EmployerMetricsModal';
import { Star } from '@mui/icons-material';

type EmployersListingRowProps = {
  row: Employer;
  spamAccount?: boolean;
  blockAccount?: boolean;
};

const getBackgroundColor = (row: Employer, blockAccount: boolean) => {
  // Check if the account is blocked then make it 'red'
  if (blockAccount) {
    return primaryRed;
  }

  if (row.company) {
    if (row.company?.plan === 3 && row.company?.enterprise_company) {
      switch (row.company.enterprise_company.plan) {
        case 0:
          return primaryRed;
        case 999:
          return yellow;
        case 1:
        case 2:
          return primaryGreen;
        default:
          return primaryGrey;
      }
    } else {
      switch (row.company?.plan) {
        case 0:
          return primaryRed;
        case 999:
          return yellow;
        case 1:
        case 2:
        case 4:
          return primaryGreen;
        default:
          return primaryGrey;
      }
    }
  } else if (row.enterprise) {
    switch (row.enterprise.plan) {
      case 0:
        return primaryRed;
      case 999:
        return yellow;
      case 1:
      case 2:
      case 4:
        return primaryGreen;
      default:
        return primaryGrey;
    }
  } else {
    return primaryGreen;
  }
};

const getLabel = (row: Employer, blockAccount: boolean) => {
  // Check if the account is blocked then return
  if (blockAccount) {
    return 'Blocked';
  }

  if (row.company) {
    if (row.company?.plan === 3 && row.company?.enterprise_company) {
      switch (row.company.enterprise_company.plan) {
        case 0:
          return 'Expired';
        case 999:
          return 'Unpaid';
        case 1:
        case 2:
        case 4:
          return 'Paid';
        default:
          return '-';
      }
    } else {
      switch (row.company.plan) {
        case 0:
          return 'Expired';
        case 999:
          return 'Unpaid';
        case 1:
        case 2:
        case 4:
          return 'Paid';
        case 3:
          return 'Paid'; // Fall back if no enterprise company logic
        default:
          return '-';
      }
    }
  } else if (row.enterprise) {
    switch (row.enterprise.plan) {
      case 0:
        return 'Expired';
      case 999:
        return 'Trial';
      case 1:
      case 2:
      case 4:
        return 'Paid';
      default:
        return '-';
    }
  } else {
    return '-';
  }
};

const EmployersListingRow: FC<EmployersListingRowProps> = ({
  row,
  spamAccount,
  blockAccount = false,
}) => {
  const [showApplicantMetricsModal, setShowEmployerModal] =
    useState<Employer | null>(null);
  return (
    <>
      <TableRow>
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              variant="text"
              size="small"
              sx={{
                textAlign: 'start',
                textTransform: 'none',
                textDecoration: 'underline',
                color: darkBlue,
                padding: 0,
                minWidth: 0,
              }}
              onClick={() => setShowEmployerModal(row)}
            >
              {row.company
                ? (row.company?.s_name ?? '-')
                : row.enterprise
                  ? row.enterprise.name
                  : '-'}
            </Button>
            {row.company && row.master_account && (
              <Tooltip title="Admin Account" arrow>
                <Star
                  sx={{
                    fontSize: 18,
                    color: primaryGreen,
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        </TableCell>
        <TableCell
          sx={{ maxWidth: '100%', wordBreak: 'break-all' }}
          align="left"
        >
          <Box sx={{ position: 'relative' }}>{row.name || '-'}</Box>
        </TableCell>
        <TableCell sx={{ wordBreak: 'break-all' }} align="left">
          {row.email || '-'}
        </TableCell>

        <TableCell sx={{ maxWidth: '15px' }}>{row.num_logins ?? '-'}</TableCell>

        <TableCell>
          {row.last_login ? dayjs(row.last_login).format('MM/DD/YY') : '-'}
        </TableCell>
        <TableCell>
          <Box
            sx={{
              backgroundColor: getBackgroundColor(row, blockAccount),
              color: white,
              padding: 0.5,
              textAlign: 'center',
              borderRadius: 5,
            }}
          >
            {getLabel(row, blockAccount)}
          </Box>
        </TableCell>
      </TableRow>
      {showApplicantMetricsModal && (
        <EmployerMetricsModal
          handleClose={() => setShowEmployerModal(null)}
          item={showApplicantMetricsModal}
          spamAccount={spamAccount}
          blockAccount={blockAccount}
        />
      )}
    </>
  );
};

export default EmployersListingRow;
